import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

// ── Organization context types ────────────────────────────────────────────────

export type IntegrationProvider = 'github' | 'jira' | 'aws' | 'azure' | 'slack' | 'teams';
export type IntegrationConnectionStatus = 'connected' | 'pending' | 'disconnected';

export interface OrgIntegration {
  provider: IntegrationProvider;
  status: IntegrationConnectionStatus;
  connectedAt: string | null;
  resourceCount: number | null;
  metadata: Record<string, string> | null;
}

export interface AdminTeamMember {
  id:         string;
  email:      string;
  givenName:  string;
  familyName: string;
  role:       string;
  status:     string;
  joinedAt:   string;
}

export interface AdminMonitoredService {
  id:          string;
  resourceId:  string;
  provider?:   string;          // 'AWS' | 'AZURE'
  serviceType: string;
  name:        string;
  isMonitored: boolean;
  accountId:   string | null;   // AWS account id / Azure subscription id
  region:      string | null;   // AWS region / Azure location
  createdAt:   string;
  updatedAt:   string;
}

// ── AWS multi-account admin types ─────────────────────────────────────────────

export type AdminAwsConnectionStatus = 'ACTIVE' | 'FAILED' | 'PENDING_VERIFICATION';
export type AdminAwsHealth           = 'OK' | 'STALE' | 'ERROR' | 'UNKNOWN';

/** One connected AWS account under a subscription, as seen by admin/support. */
export interface AdminAwsAccount {
  accountId:              string;
  label?:                 string;
  roleArn?:               string;   // visible to admins for support; externalId is never exposed
  isPrimary:              boolean;
  connectionStatus:       AdminAwsConnectionStatus;
  healthStatus:           AdminAwsHealth;
  defaultRegion?:         string;
  enabledRegions:         string[];
  connectedAt?:           string;
  updatedAt?:             string;
  lastSyncAt?:            string;
  failureReason?:         string;
  sourceConnectionId:     string;
  /** Per-account monitoring footprint (from the account's service entries). */
  monitoredResourceCount: number;
  serviceEntryCount:      number;
  activeRegions:          string[];
}

/** Compact roll-up so support can tell "no AWS" from "connected but degraded". */
export interface AdminAwsSummary {
  connected:        boolean;
  accountCount:     number;
  activeCount:      number;
  primaryAccountId: string | null;
  hasPrimary:       boolean;
  degraded:         boolean;
}

/** Result of a support-initiated re-verify. */
export interface AdminAwsVerifyResult {
  found:          boolean;
  accountId:      string;
  status?:        'ACTIVE' | 'FAILED';
  failureReason?: string;
}

/** Result of a support-initiated disconnect (one account). */
export interface AdminAwsDisconnectResult {
  message:            string;
  accountId:          string;
  promotedAccountId?: string;
  cleanup:            { configsRemoved: number; entriesRemoved: number; quotaFreed: number; cleanupErrors: number };
}

/** One AWS lifecycle audit event (disconnect / promotion / verify / region edit). */
export interface AdminAwsEvent {
  id:        string;
  timestamp: string;
  eventType: string;
  status:    string;
  message:   string;
  accountId: string | null;
  metadata:  Record<string, unknown>;
}

/** Result of a support-initiated Azure subscription re-verify (parallel to AdminAwsVerifyResult). */
export interface AdminAzureVerifyResult {
  found:          boolean;
  subscriptionId: string;
  status?:        'ACTIVE' | 'FAILED';
  failureReason?: string;
}

/** Result of a support-initiated Azure subscription disconnect. */
export interface AdminAzureDisconnectResult {
  message:                 string;
  subscriptionId:          string;
  promotedSubscriptionId?: string;
  cleanup:                 { configsRemoved: number; entriesRemoved: number; quotaFreed: number; cleanupErrors: number };
}

/** One Azure subscription lifecycle audit event (verify / disconnect / promotion / location edit). */
export interface AdminAzureEvent {
  id:             string;
  timestamp:      string;
  eventType:      string;
  status:         string;
  message:        string;
  subscriptionId: string | null;
  metadata:       Record<string, unknown>;
}

// ── Azure multi-subscription admin types ──────────────────────────────────────

export type AdminAzureConnectionStatus = 'ACTIVE' | 'FAILED' | 'PENDING_VERIFICATION';
export type AdminAzureHealth           = 'OK' | 'STALE' | 'ERROR' | 'UNKNOWN';

/** One connected Azure subscription under the integration, as seen by admin/support. */
export interface AdminAzureSubscription {
  subscriptionId:         string;
  label?:                 string;
  isPrimary:              boolean;
  connectionStatus:       AdminAzureConnectionStatus;
  healthStatus:           AdminAzureHealth;
  defaultLocation?:       string;
  enabledLocations:       string[];
  connectedAt?:           string;
  updatedAt?:             string;
  lastSyncAt?:            string;
  failureReason?:         string;
  sourceConnectionId:     string;
  monitoredResourceCount: number;
  serviceEntryCount:      number;
  activeLocations:        string[];
}

/** Compact Azure roll-up (parallel to AdminAwsSummary). */
export interface AdminAzureSummary {
  connected:             boolean;
  subscriptionCount:     number;
  activeCount:           number;
  primarySubscriptionId: string | null;
  hasPrimary:            boolean;
  degraded:              boolean;
}

/** Result of a support-initiated GCP project re-verify (parallel to AdminAzureVerifyResult). */
export interface AdminGcpVerifyResult {
  found:          boolean;
  projectId:      string;
  status?:        'ACTIVE' | 'FAILED';
  failureReason?: string;
}

/** Result of a support-initiated GCP project disconnect. */
export interface AdminGcpDisconnectResult {
  message:            string;
  projectId:          string;
  promotedProjectId?: string;
  cleanup:            { configsRemoved: number; entriesRemoved: number; quotaFreed: number; cleanupErrors: number };
}

/** One GCP project lifecycle audit event (verify / disconnect / promotion / region edit). */
export interface AdminGcpEvent {
  id:        string;
  timestamp: string;
  eventType: string;
  status:    string;
  message:   string;
  projectId: string | null;
  metadata:  Record<string, unknown>;
}

// ── GCP multi-project admin types ─────────────────────────────────────────────

export type AdminGcpConnectionStatus = 'ACTIVE' | 'FAILED' | 'PENDING_VERIFICATION';
export type AdminGcpHealth           = 'OK' | 'STALE' | 'ERROR' | 'UNKNOWN';

/** One connected GCP project under the integration, as seen by admin/support. */
export interface AdminGcpProject {
  projectId:              string;
  label?:                 string;
  clientEmail?:           string;
  isPrimary:              boolean;
  connectionStatus:       AdminGcpConnectionStatus;
  healthStatus:           AdminGcpHealth;
  defaultRegion?:         string;
  enabledRegions:         string[];
  connectedAt?:           string;
  updatedAt?:             string;
  lastSyncAt?:            string;
  failureReason?:         string;
  sourceConnectionId:     string;
  monitoredResourceCount: number;
  serviceEntryCount:      number;
  activeRegions:          string[];
}

/** Compact GCP roll-up (parallel to AdminAwsSummary / AdminAzureSummary). */
export interface AdminGcpSummary {
  connected:        boolean;
  projectCount:     number;
  activeCount:      number;
  primaryProjectId: string | null;
  hasPrimary:       boolean;
  degraded:         boolean;
}

export interface OrgContext {
  organization: {
    orgId: string;
    name: string;
    subscriptionTier: string;
    subscriptionStatus: string;
    ownerId: string;
    nextRenewalDate: string | null;
    createdAt: string;
    updatedAt: string;
  };
  teamSize: number;
  monitoredServicesCount: number;
  teamMembers: AdminTeamMember[];
  monitoredServices: AdminMonitoredService[];
  integrations: OrgIntegration[];
  /** Full per-account AWS inventory (multi-account aware). */
  awsAccounts?: AdminAwsAccount[];
  awsSummary?: AdminAwsSummary;
  /** Full per-subscription Azure inventory. */
  azureSubscriptions?: AdminAzureSubscription[];
  azureSummary?: AdminAzureSummary;
  /** Full per-project GCP inventory. */
  gcpProjects?: AdminGcpProject[];
  gcpSummary?: AdminGcpSummary;
}

// ── Subscription types ────────────────────────────────────────────────────────

export type OrgSubscriptionTier   = 'FREE' | 'PRO' | 'ENTERPRISE';
export type OrgSubscriptionStatus = 'ACTIVE' | 'PAST_DUE' | 'CANCELED';

export interface OrgSubscription {
  organizationId: string;
  organizationName: string;
  tier: OrgSubscriptionTier;
  status: OrgSubscriptionStatus;
  teamSize: number;
  nextRenewalDate: string | null;
  monitoredServicesCount: number;
}

export interface ListSubscriptionsResponse {
  subscriptions: OrgSubscription[];
  total: number;
  nextPageToken: string | null;
}

export interface PatchOrgSubscriptionDto {
  tier?: string;
  status?: string;
  nextRenewalDate?: string | null;
}

export interface PatchedOrgSubscription {
  organizationId: string;
  organizationName: string;
  tier: string;
  status: string;
  nextRenewalDate: string | null;
}

// ── Org monitoring types ──────────────────────────────────────────────────────

export type MonitoringHealth = 'HEALTHY' | 'UNHEALTHY' | 'UNKNOWN' | 'NOT_CONFIGURED';

export interface AdminMonitoringServiceState {
  serviceEntryId:   string;
  serviceName:      string;
  serviceType:      string;
  isMonitored:      boolean;
  accountId:        string | null;
  region:           string | null;
  configId:         string | null;
  monitoringMode:   string | null;
  health:           MonitoringHealth;
  lastCheckedAt:    string | null;
  lastObservedAt:   string | null;
  consecutiveErrors: number;
  openFindingCount: number;
}

export interface AdminMonitoringSummary {
  totalMonitored:     number;
  healthyCount:       number;
  degradedCount:      number;
  unknownCount:       number;
  notConfiguredCount: number;
  lastCheckedAt:      string | null;
}

export interface AdminOrgMonitoring {
  summary:  AdminMonitoringSummary;
  services: AdminMonitoringServiceState[];
}

export type FindingSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
export type FindingState    = 'OPEN' | 'RESOLVED';

export interface AdminMonitoringFinding {
  findingId:      string;
  configId:       string;
  serviceEntryId: string;
  serviceName:    string;
  serviceType:    string;
  accountId:      string;
  region:         string;
  severity:       FindingSeverity;
  findingType:    string;
  findingState:   FindingState;
  title:          string;
  summary:        string;
  source:         string;
  observedAt:     string;
  createdAt:      string;
  resolvedAt:     string | null;
}

export interface OrgMonitoringFindingsResponse {
  findings: AdminMonitoringFinding[];
  total:    number;
  hasMore:  boolean;
}

// ── System log types ──────────────────────────────────────────────────────────

export type SyslogSeverity      = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'CRITICAL';
export type SyslogComponentType = 'DISPATCHER' | 'WORKER' | 'QUEUE' | 'INTEGRATION' | 'RECONCILIATION' | 'INFRA' | 'SECURITY' | 'API' | 'SCHEDULER';
export type SyslogStatus        = 'SUCCESS' | 'FAILURE' | 'IN_PROGRESS' | 'SKIPPED';

export interface SystemLogEvent {
  id:            string;
  timestamp:     string;
  severity:      SyslogSeverity;
  component:     SyslogComponentType;
  eventType:     string;
  message:       string;
  status:        SyslogStatus;
  correlationId: string | null;
  tenantId:      string | null;
  orgId:         string | null;
  provider:      string | null;
  resourceId:    string | null;
  findingId:     string | null;
  deliveryId:    string | null;
  errorSummary:  string | null;
  metadata:      Record<string, unknown>;
}

export interface SystemLogsResponse {
  events: SystemLogEvent[];
  count:  number;
  days:   number;
}

export interface SystemLogsParams {
  days?:          number;
  correlationId?: string;
  severity?:      SyslogSeverity;
  component?:     SyslogComponentType;
  eventType?:     string;
}

export interface SystemLogHealthResponse {
  total:       number;
  errorCount:  number;
  bySeverity:  Record<string, number>;
  byComponent: Record<string, number>;
  windowHours: number;
}

// ── Cross-org findings types ──────────────────────────────────────────────────

export type AdminFindingSeverity = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW' | 'INFO';
export type AdminFindingState    = 'OPEN' | 'RESOLVED';
/**
 * Service type is an open string — the product's supported service set grows
 * over time (Lambda, Glue, CodePipeline, Step Functions, ECS, RDS, API Gateway,
 * EC2, Cognito, AppSync, DynamoDB, SQS, EventBridge, ALB, SNS, CloudFront, …).
 * The UI renders any type dynamically; `KNOWN_SERVICE_TYPES` is only a
 * convenience list for filter dropdowns and must not gate display.
 */
export type AdminFindingServiceType = string;

/** The service types currently shipped (AWS + Azure) — seeds filter dropdowns, does not gate rendering. */
export const KNOWN_SERVICE_TYPES: string[] = [
  // AWS
  'LAMBDA', 'GLUE', 'PIPELINE', 'STEP_FUNCTIONS', 'ECS_SERVICE', 'RDS_INSTANCE',
  'API_GATEWAY', 'EC2_INSTANCE', 'COGNITO', 'APPSYNC', 'DYNAMODB', 'SQS',
  'EVENTBRIDGE', 'ALB', 'SNS', 'CLOUDFRONT',
  // Azure
  'AZURE_FUNCTIONS', 'AZURE_API_MANAGEMENT', 'AZURE_FRONT_DOOR', 'AZURE_COSMOS_DB',
  'AZURE_SQL_DATABASE', 'AZURE_VM', 'AZURE_APPLICATION_GATEWAY', 'AZURE_CONTAINER_APPS',
  'AZURE_SERVICE_BUS', 'AZURE_EVENT_GRID', 'AZURE_DATA_FACTORY', 'AZURE_LOGIC_APPS',
  'AZURE_APP_SERVICE', 'AZURE_APPLICATION_INSIGHTS', 'AZURE_CACHE_REDIS', 'AZURE_KUBERNETES_SERVICE',
  'AZURE_EVENT_HUBS', 'AZURE_KEY_VAULT', 'AZURE_LOAD_BALANCER', 'AZURE_STORAGE_ACCOUNT',
  // Google Cloud
  'GCP_API_GATEWAY', 'GCP_CLOUD_CDN', 'GCP_CLOUD_DATA_FUSION', 'GCP_CLOUD_LOAD_BALANCING',
  'GCP_CLOUD_RUN', 'GCP_CLOUD_RUN_FUNCTIONS', 'GCP_CLOUD_SQL', 'GCP_COMPUTE_ENGINE',
  'GCP_EVENTARC', 'GCP_FIRESTORE', 'GCP_PUBSUB', 'GCP_WORKFLOWS',
  'GCP_GKE', 'GCP_MEMORYSTORE_REDIS', 'GCP_SECRET_MANAGER', 'GCP_CLOUD_STORAGE',
  'GCP_BIGQUERY', 'GCP_ARTIFACT_REGISTRY', 'GCP_CLOUD_TASKS', 'GCP_CLOUD_ARMOR',
];

/** Human labels for known service types. Unknown types are humanized from the raw enum. */
export const SERVICE_TYPE_LABELS: Record<string, string> = {
  // AWS
  LAMBDA: 'Lambda', GLUE: 'Glue', PIPELINE: 'CodePipeline', STEP_FUNCTIONS: 'Step Functions',
  ECS_SERVICE: 'ECS', RDS_INSTANCE: 'RDS', API_GATEWAY: 'API Gateway', EC2_INSTANCE: 'EC2',
  COGNITO: 'Cognito', APPSYNC: 'AppSync', DYNAMODB: 'DynamoDB', SQS: 'SQS',
  EVENTBRIDGE: 'EventBridge', ALB: 'ALB', SNS: 'SNS', CLOUDFRONT: 'CloudFront',
  // Azure
  AZURE_FUNCTIONS: 'Azure Functions', AZURE_API_MANAGEMENT: 'API Management',
  AZURE_FRONT_DOOR: 'Front Door', AZURE_COSMOS_DB: 'Cosmos DB',
  AZURE_SQL_DATABASE: 'Azure SQL Database', AZURE_VM: 'Virtual Machines',
  AZURE_APPLICATION_GATEWAY: 'Application Gateway', AZURE_CONTAINER_APPS: 'Container Apps',
  AZURE_SERVICE_BUS: 'Service Bus', AZURE_EVENT_GRID: 'Event Grid',
  AZURE_DATA_FACTORY: 'Data Factory', AZURE_LOGIC_APPS: 'Logic Apps',
  AZURE_APP_SERVICE: 'App Service', AZURE_APPLICATION_INSIGHTS: 'Application Insights',
  AZURE_CACHE_REDIS: 'Azure Cache for Redis', AZURE_KUBERNETES_SERVICE: 'Azure Kubernetes Service',
  AZURE_EVENT_HUBS: 'Event Hubs', AZURE_KEY_VAULT: 'Key Vault',
  AZURE_LOAD_BALANCER: 'Load Balancer', AZURE_STORAGE_ACCOUNT: 'Storage Accounts',
  // Google Cloud
  GCP_API_GATEWAY: 'API Gateway', GCP_CLOUD_CDN: 'Cloud CDN',
  GCP_CLOUD_DATA_FUSION: 'Cloud Data Fusion', GCP_CLOUD_LOAD_BALANCING: 'Cloud Load Balancing',
  GCP_CLOUD_RUN: 'Cloud Run', GCP_CLOUD_RUN_FUNCTIONS: 'Cloud Run functions',
  GCP_CLOUD_SQL: 'Cloud SQL', GCP_COMPUTE_ENGINE: 'Compute Engine',
  GCP_EVENTARC: 'Eventarc', GCP_FIRESTORE: 'Firestore',
  GCP_PUBSUB: 'Pub/Sub', GCP_WORKFLOWS: 'Workflows',
  GCP_GKE: 'Google Kubernetes Engine', GCP_MEMORYSTORE_REDIS: 'Memorystore for Redis',
  GCP_SECRET_MANAGER: 'Secret Manager', GCP_CLOUD_STORAGE: 'Cloud Storage',
  GCP_BIGQUERY: 'BigQuery', GCP_ARTIFACT_REGISTRY: 'Artifact Registry',
  GCP_CLOUD_TASKS: 'Cloud Tasks', GCP_CLOUD_ARMOR: 'Cloud Armor',
};

/** The cloud provider that owns a service type (derived from the AZURE_/GCP_ prefix). */
export function providerForServiceType(serviceType: string): 'AWS' | 'AZURE' | 'GCP' {
  const t = serviceType?.toUpperCase();
  if (t?.startsWith('AZURE_')) return 'AZURE';
  if (t?.startsWith('GCP_'))   return 'GCP';
  return 'AWS';
}

/**
 * Humanized display label for ANY service type. Falls back to Title-Casing the
 * raw enum (e.g. `BEDROCK_AGENT` → `Bedrock Agent`) so newer/unknown types that
 * appear in persisted data still render cleanly instead of disappearing.
 */
export function humanizeServiceType(type: string): string {
  if (!type) return '—';
  const upper = type.toUpperCase();
  return SERVICE_TYPE_LABELS[upper]
    ?? upper.split('_').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
}

/**
 * Badge tint per service type (AWS + Azure). Unknown types fall back to a neutral
 * badge, so the admin UI never breaks on a newly-added service.
 */
export const SERVICE_TYPE_BADGE: Record<string, string> = {
  // AWS
  LAMBDA: 'bg-orange-100 text-orange-700', GLUE: 'bg-sky-100 text-sky-700',
  PIPELINE: 'bg-teal-100 text-teal-700', STEP_FUNCTIONS: 'bg-purple-100 text-purple-700',
  ECS_SERVICE: 'bg-cyan-100 text-cyan-700', RDS_INSTANCE: 'bg-blue-100 text-blue-700',
  API_GATEWAY: 'bg-indigo-100 text-indigo-700', EC2_INSTANCE: 'bg-amber-100 text-amber-700',
  COGNITO: 'bg-pink-100 text-pink-700', APPSYNC: 'bg-fuchsia-100 text-fuchsia-700',
  DYNAMODB: 'bg-violet-100 text-violet-700', SQS: 'bg-rose-100 text-rose-700',
  EVENTBRIDGE: 'bg-lime-100 text-lime-700', ALB: 'bg-emerald-100 text-emerald-700',
  SNS: 'bg-red-100 text-red-700', CLOUDFRONT: 'bg-slate-200 text-slate-700',
  // Azure
  AZURE_FUNCTIONS: 'bg-sky-100 text-sky-700', AZURE_API_MANAGEMENT: 'bg-indigo-100 text-indigo-700',
  AZURE_FRONT_DOOR: 'bg-blue-100 text-blue-700', AZURE_COSMOS_DB: 'bg-violet-100 text-violet-700',
  AZURE_SQL_DATABASE: 'bg-cyan-100 text-cyan-700', AZURE_VM: 'bg-amber-100 text-amber-700',
  AZURE_APPLICATION_GATEWAY: 'bg-emerald-100 text-emerald-700', AZURE_CONTAINER_APPS: 'bg-cyan-100 text-cyan-700',
  AZURE_SERVICE_BUS: 'bg-rose-100 text-rose-700', AZURE_EVENT_GRID: 'bg-lime-100 text-lime-700',
  AZURE_DATA_FACTORY: 'bg-sky-100 text-sky-700', AZURE_LOGIC_APPS: 'bg-purple-100 text-purple-700',
  AZURE_APP_SERVICE: 'bg-indigo-100 text-indigo-700', AZURE_APPLICATION_INSIGHTS: 'bg-teal-100 text-teal-700',
  AZURE_CACHE_REDIS: 'bg-red-100 text-red-700', AZURE_KUBERNETES_SERVICE: 'bg-cyan-100 text-cyan-700',
  AZURE_EVENT_HUBS: 'bg-lime-100 text-lime-700', AZURE_KEY_VAULT: 'bg-amber-100 text-amber-700',
  AZURE_LOAD_BALANCER: 'bg-emerald-100 text-emerald-700', AZURE_STORAGE_ACCOUNT: 'bg-slate-200 text-slate-700',
  // Google Cloud
  GCP_API_GATEWAY: 'bg-indigo-100 text-indigo-700', GCP_CLOUD_CDN: 'bg-blue-100 text-blue-700',
  GCP_CLOUD_DATA_FUSION: 'bg-sky-100 text-sky-700', GCP_CLOUD_LOAD_BALANCING: 'bg-emerald-100 text-emerald-700',
  GCP_CLOUD_RUN: 'bg-cyan-100 text-cyan-700', GCP_CLOUD_RUN_FUNCTIONS: 'bg-amber-100 text-amber-700',
  GCP_CLOUD_SQL: 'bg-blue-100 text-blue-700', GCP_COMPUTE_ENGINE: 'bg-amber-100 text-amber-700',
  GCP_EVENTARC: 'bg-lime-100 text-lime-700', GCP_FIRESTORE: 'bg-violet-100 text-violet-700',
  GCP_PUBSUB: 'bg-rose-100 text-rose-700', GCP_WORKFLOWS: 'bg-purple-100 text-purple-700',
  GCP_GKE: 'bg-cyan-100 text-cyan-700', GCP_MEMORYSTORE_REDIS: 'bg-red-100 text-red-700',
  GCP_SECRET_MANAGER: 'bg-amber-100 text-amber-700', GCP_CLOUD_STORAGE: 'bg-slate-200 text-slate-700',
  GCP_BIGQUERY: 'bg-sky-100 text-sky-700', GCP_ARTIFACT_REGISTRY: 'bg-teal-100 text-teal-700',
  GCP_CLOUD_TASKS: 'bg-rose-100 text-rose-700', GCP_CLOUD_ARMOR: 'bg-indigo-100 text-indigo-700',
};

/**
 * lucide icon per service type. Only icon names already registered/used in the
 * admin app are referenced, so new services never render a blank glyph.
 */
export const SERVICE_TYPE_ICON: Record<string, string> = {
  // AWS
  LAMBDA: 'zap', GLUE: 'table-2', PIPELINE: 'git-merge', STEP_FUNCTIONS: 'git-branch',
  ECS_SERVICE: 'box', RDS_INSTANCE: 'database', API_GATEWAY: 'globe', EC2_INSTANCE: 'server',
  COGNITO: 'users', APPSYNC: 'workflow', DYNAMODB: 'table', SQS: 'inbox',
  EVENTBRIDGE: 'calendar-clock', ALB: 'scale', SNS: 'megaphone', CLOUDFRONT: 'cloud',
  // Azure
  AZURE_FUNCTIONS: 'zap', AZURE_API_MANAGEMENT: 'globe', AZURE_FRONT_DOOR: 'cloud',
  AZURE_COSMOS_DB: 'table', AZURE_SQL_DATABASE: 'database', AZURE_VM: 'server',
  AZURE_APPLICATION_GATEWAY: 'scale', AZURE_CONTAINER_APPS: 'box', AZURE_SERVICE_BUS: 'inbox',
  AZURE_EVENT_GRID: 'calendar-clock', AZURE_DATA_FACTORY: 'table-2', AZURE_LOGIC_APPS: 'git-branch',
  AZURE_APP_SERVICE: 'globe', AZURE_APPLICATION_INSIGHTS: 'activity', AZURE_CACHE_REDIS: 'database',
  AZURE_KUBERNETES_SERVICE: 'box', AZURE_EVENT_HUBS: 'megaphone', AZURE_KEY_VAULT: 'key',
  AZURE_LOAD_BALANCER: 'scale', AZURE_STORAGE_ACCOUNT: 'database',
  // Google Cloud
  GCP_API_GATEWAY: 'globe', GCP_CLOUD_CDN: 'cloud', GCP_CLOUD_DATA_FUSION: 'table-2',
  GCP_CLOUD_LOAD_BALANCING: 'scale', GCP_CLOUD_RUN: 'box', GCP_CLOUD_RUN_FUNCTIONS: 'zap',
  GCP_CLOUD_SQL: 'database', GCP_COMPUTE_ENGINE: 'server', GCP_EVENTARC: 'megaphone',
  GCP_FIRESTORE: 'table', GCP_PUBSUB: 'inbox', GCP_WORKFLOWS: 'git-branch',
  GCP_GKE: 'box', GCP_MEMORYSTORE_REDIS: 'database', GCP_SECRET_MANAGER: 'key',
  GCP_CLOUD_STORAGE: 'hard-drive', GCP_BIGQUERY: 'table-2', GCP_ARTIFACT_REGISTRY: 'box',
  GCP_CLOUD_TASKS: 'list-todo', GCP_CLOUD_ARMOR: 'shield',
};

/** Badge tint for a service type (neutral fallback for unknown types). */
export function serviceTypeBadgeClass(type: string): string {
  return SERVICE_TYPE_BADGE[type?.toUpperCase()] ?? 'bg-zinc-100 text-zinc-600';
}

/** lucide icon for a service type (generic fallback for unknown types). */
export function serviceTypeIcon(type: string): string {
  return SERVICE_TYPE_ICON[type?.toUpperCase()] ?? 'cpu';
}

/** True for account-/subscription-/project-scoped (region-less) services. */
export function isGlobalServiceType(type: string): boolean {
  const t = type?.toUpperCase();
  return t === 'CLOUDFRONT' || t === 'AZURE_FRONT_DOOR'
    || t === 'GCP_CLOUD_CDN' || t === 'GCP_SECRET_MANAGER' || t === 'GCP_CLOUD_ARMOR';
}

// ── Cloud provider metadata — the single source of provider-native terminology ──
// so the admin UI renders "account"/"region" for AWS and "subscription"/"location"
// for Azure without provider conditionals scattered through components.

export type CloudProviderId = 'AWS' | 'AZURE' | 'GCP';

export interface CloudProviderMeta {
  id:              CloudProviderId;
  label:           string;  // 'AWS' / 'Azure' / 'Google Cloud'
  scopeNoun:       string;  // 'account' / 'subscription' / 'project'
  scopeNounPlural: string;  // 'accounts' / 'subscriptions' / 'projects'
  geoNoun:         string;  // 'region' / 'location' / 'region'
  geoNounPlural:   string;  // 'regions' / 'locations' / 'regions'
  connectHow:      string;  // how the tenant connects the provider
  icon:            string;  // lucide icon name
}

export const CLOUD_PROVIDER_META: Record<CloudProviderId, CloudProviderMeta> = {
  AWS: {
    id: 'AWS', label: 'AWS', scopeNoun: 'account', scopeNounPlural: 'accounts',
    geoNoun: 'region', geoNounPlural: 'regions', connectHow: 'cross-account IAM role', icon: 'cloud',
  },
  AZURE: {
    id: 'AZURE', label: 'Azure', scopeNoun: 'subscription', scopeNounPlural: 'subscriptions',
    geoNoun: 'location', geoNounPlural: 'locations', connectHow: 'service principal', icon: 'cloud',
  },
  GCP: {
    id: 'GCP', label: 'Google Cloud', scopeNoun: 'project', scopeNounPlural: 'projects',
    geoNoun: 'region', geoNounPlural: 'regions', connectHow: 'service account', icon: 'cloud',
  },
};

export function providerMeta(provider: string): CloudProviderMeta {
  return CLOUD_PROVIDER_META[(provider?.toUpperCase() as CloudProviderId)] ?? CLOUD_PROVIDER_META.AWS;
}

export interface AdminFinding {
  findingId:    string;
  ownerId:      string;
  orgId:        string | null;
  orgName:      string | null;
  provider?:    string;   // 'AWS' | 'AZURE'
  serviceType:  string;
  resourceId:   string;
  accountId:    string;
  region:       string;
  severity:     AdminFindingSeverity;
  findingState: AdminFindingState;
  findingType:  string;
  title:        string;
  summary:      string;
  fingerprint:  string;
  source:       string;
  configId:     string;
  createdAt:    string;
  observedAt:   string;
  resolvedAt:   string | null;
}

export interface AdminFindingDetail extends AdminFinding {
  details: Record<string, unknown>;
}

export interface ListFindingsResponse {
  findings:   AdminFinding[];
  nextCursor: string | null;
  count:      number;
}

export interface ListFindingsParams {
  severity?:    string;
  state?:       string;
  serviceType?: string;
  orgId?:       string;
  limit?:       number;
  cursor?:      string;
}

// ── Monitoring rules types ────────────────────────────────────────────────────

export type MonitoringRuleScope       = 'GLOBAL' | 'SERVICE_TYPE';
/**
 * Any supported service type. Kept as a string (not a narrow union) so the page
 * is data-driven and never drops a newer/unknown service type present in
 * persisted override data. The backend validates the value on save.
 */
export type MonitoringRuleServiceType = string;

export interface MonitoringRule {
  scope:                        MonitoringRuleScope;
  serviceType:                  MonitoringRuleServiceType | null;
  overrideEnabled:              boolean;
  enabled:                      boolean;
  evaluationWindowMinutes:      number;
  metricPeriodSeconds:          number;
  errorRateThresholdPercent:    number;
  minimumSampleCount:           number;
  throttleCountThreshold:       number;
  severityHighThresholdPercent: number;
  updatedBy:                    string | null;
  updatedAt:                    string;
}

export interface MonitoringRulesResponse {
  rules: MonitoringRule[];
}

export interface MonitoringRuleDetailResponse {
  global:   MonitoringRule;
  override: MonitoringRule | null;
}

export type MonitoringRulePayload = Pick<
  MonitoringRule,
  | 'enabled'
  | 'evaluationWindowMinutes'
  | 'metricPeriodSeconds'
  | 'errorRateThresholdPercent'
  | 'minimumSampleCount'
  | 'throttleCountThreshold'
  | 'severityHighThresholdPercent'
> & { overrideEnabled?: boolean };

// ── Scheduler config types ────────────────────────────────────────────────────

export interface SchedulerConfig {
  frequencyMinutes: number | null;
  expression: string;
  state: string;
}

// ── Monitoring types ──────────────────────────────────────────────────────────

export interface TierConfig {
  tier: string;
  displayName: string;
  maxMonitoredServices: number;
  maxTeamMembers: number;
  monthlyCost: number;
  sortOrder: number;
  isPublic: boolean;
  isCustom: boolean;
  updatedAt: string;
  updatedBy?: string;
}

export interface MonitoringOverviewRow {
  userId: string;
  email: string;
  tier: string;
  monitoredCount: number;
  limit: number;
  remaining: number;
  percentUsed: number;
  updatedAt: string;
}

export interface ServiceEntry {
  userId: string;
  serviceType: string;
  resourceId: string;
  isMonitored: boolean;
  metadata: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

// ── User types ────────────────────────────────────────────────────────────────

export interface DynamoUser {
  PK: string;
  SK: string;
  id: string;
  email: string;
  givenName: string;
  familyName: string;
  status: string;
  role?: string;
  subscriptionTier?: string;
  subscriptionStatus?: string;
  orgId?: string | null;
  orgName?: string | null;
  orgRole?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  GSI1PK?: string;
}

export interface ImpersonationSession {
  sessionToken: string;
  targetUserId: string;
  targetEmail: string;
  targetName: string;
  expiresAt: string;
  actorAdminEmail: string;
}

export interface PatchSubscriptionDto {
  subscriptionTier?: string;
  subscriptionStatus?: string;
}

export interface ListUsersResponse {
  users: DynamoUser[];
  count: number;
  nextPageToken: string | null;
}

export interface PatchUserDto {
  status?: string;
  role?: string;
  givenName?: string;
  familyName?: string;
}

export interface CreateAdminUserDto {
  email: string;
  givenName: string;
  familyName: string;
  temporaryPassword: string;
}

// ── Bug reports ─────────────────────────────────────────────────────────────

export type BugReportStatus = 'NEW' | 'IN_PROGRESS' | 'RESOLVED' | 'DISMISSED';

export interface BugReport {
  id:            string;
  status:        BugReportStatus;
  description:   string;
  pageUrl:       string;
  userAgent:     string;
  viewport:      string;
  reporterId:    string;
  reporterEmail: string;
  reporterName:  string;
  orgId:         string | null;
  createdAt:     string;
  updatedAt:     string;
  updatedBy:     string | null;
}

export interface BugReportsResponse {
  bugReports: BugReport[];
  nextCursor: string | null;
  /** Reports per status across all reports, independent of the current filter. */
  counts:     Record<BugReportStatus, number>;
}

export interface BugReportsParams {
  status?: BugReportStatus;
  cursor?: string;
  limit?:  number;
}

@Injectable({ providedIn: 'root' })
export class AdminService {
  private readonly base = `${environment.apiUrl}/admin`;

  constructor(private readonly http: HttpClient) {}

  listUsers(pageToken?: string, includeDeleted = false): Observable<ListUsersResponse> {
    let params = new HttpParams(pageToken ? { fromObject: { pageToken } } : {});
    if (includeDeleted) params = params.set('includeDeleted', 'true');
    return this.http.get<ListUsersResponse>(`${this.base}/users`, { params });
  }

  getUser(id: string): Observable<DynamoUser> {
    return this.http.get<DynamoUser>(`${this.base}/users/${id}`);
  }

  updateUser(id: string, dto: PatchUserDto): Observable<DynamoUser> {
    return this.http.patch<DynamoUser>(`${this.base}/users/${id}`, dto);
  }

  createUser(dto: CreateAdminUserDto): Observable<DynamoUser> {
    return this.http.post<DynamoUser>(`${this.base}/users`, dto);
  }

  deleteUser(id: string): Observable<{ message: string; deletedAt: string }> {
    return this.http.delete<{ message: string; deletedAt: string }>(`${this.base}/users/${id}`);
  }

  updateSubscription(id: string, dto: PatchSubscriptionDto): Observable<DynamoUser> {
    return this.http.patch<DynamoUser>(`${this.base}/users/${id}/subscription`, dto);
  }

  restoreUser(id: string): Observable<DynamoUser> {
    return this.http.post<DynamoUser>(`${this.base}/users/${id}/restore`, {});
  }

  resetPassword(id: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.base}/users/${id}/reset-password`, {});
  }

  startImpersonation(id: string): Observable<ImpersonationSession> {
    return this.http.post<ImpersonationSession>(`${this.base}/users/${id}/impersonate`, {});
  }

  // ── Subscription API ─────────────────────────────────────────────────

  listSubscriptions(params?: { pageToken?: string; search?: string; tier?: string }): Observable<ListSubscriptionsResponse> {
    let p = new HttpParams();
    if (params?.pageToken) p = p.set('pageToken', params.pageToken);
    if (params?.search)    p = p.set('search',    params.search);
    if (params?.tier)      p = p.set('tier',      params.tier);
    return this.http.get<ListSubscriptionsResponse>(`${this.base}/subscriptions`, { params: p });
  }

  updateOrgSubscription(organizationId: string, dto: PatchOrgSubscriptionDto): Observable<PatchedOrgSubscription> {
    return this.http.patch<PatchedOrgSubscription>(`${this.base}/subscriptions/${organizationId}`, dto);
  }

  getOrgContext(orgId: string): Observable<OrgContext> {
    return this.http.get<OrgContext>(`${this.base}/organizations/${orgId}/context`);
  }

  // ── AWS account admin actions (support-initiated, account-scoped) ──────────

  /** Re-verify one AWS account by re-assuming its stored role. */
  adminVerifyAwsAccount(orgId: string, accountId: string): Observable<AdminAwsVerifyResult> {
    return this.http.post<AdminAwsVerifyResult>(`${this.base}/organizations/${orgId}/aws/accounts/${accountId}/verify`, {});
  }

  /** Disconnect one AWS account (monitoring teardown + quota release + promotion). */
  adminDisconnectAwsAccount(orgId: string, accountId: string): Observable<AdminAwsDisconnectResult> {
    return this.http.delete<AdminAwsDisconnectResult>(`${this.base}/organizations/${orgId}/aws/accounts/${accountId}`);
  }

  /** Update one AWS account's default + enabled regions. */
  adminUpdateAwsAccountRegions(orgId: string, accountId: string, defaultRegion: string, enabledRegions: string[]): Observable<{ accountId: string; defaultRegion: string; enabledRegions: string[] }> {
    return this.http.patch<{ accountId: string; defaultRegion: string; enabledRegions: string[] }>(
      `${this.base}/organizations/${orgId}/aws/accounts/${accountId}/regions`,
      { defaultRegion, enabledRegions },
    );
  }

  /** Recent AWS lifecycle audit events for a subscription. */
  adminGetAwsEvents(orgId: string, days = 7): Observable<{ events: AdminAwsEvent[]; count: number; days: number }> {
    return this.http.get<{ events: AdminAwsEvent[]; count: number; days: number }>(
      `${this.base}/organizations/${orgId}/aws/events`, { params: { days: String(days) } },
    );
  }

  // ── Azure subscription admin actions (support-initiated, subscription-scoped) ──
  // The Azure analog of the AWS account actions above — same shared management
  // pattern, provider-native identifiers (subscription, not account).

  /** Re-verify one Azure subscription using its stored service principal. */
  adminVerifyAzureSubscription(orgId: string, subscriptionId: string): Observable<AdminAzureVerifyResult> {
    return this.http.post<AdminAzureVerifyResult>(`${this.base}/organizations/${orgId}/azure/subscriptions/${subscriptionId}/verify`, {});
  }

  /** Disconnect one Azure subscription (monitoring teardown + quota release + promotion). */
  adminDisconnectAzureSubscription(orgId: string, subscriptionId: string): Observable<AdminAzureDisconnectResult> {
    return this.http.delete<AdminAzureDisconnectResult>(`${this.base}/organizations/${orgId}/azure/subscriptions/${subscriptionId}`);
  }

  /** Recent Azure subscription lifecycle audit events for a subscription. */
  adminGetAzureEvents(orgId: string, days = 7): Observable<{ events: AdminAzureEvent[]; count: number; days: number }> {
    return this.http.get<{ events: AdminAzureEvent[]; count: number; days: number }>(
      `${this.base}/organizations/${orgId}/azure/events`, { params: { days: String(days) } },
    );
  }

  // ── GCP project admin actions (support-initiated, project-scoped) ─────────────
  // The GCP analog of the AWS/Azure account actions above — same shared management
  // pattern, provider-native identifiers (project, not account/subscription).

  /** Re-verify one GCP project using its stored service account. */
  adminVerifyGcpProject(orgId: string, projectId: string): Observable<AdminGcpVerifyResult> {
    return this.http.post<AdminGcpVerifyResult>(`${this.base}/organizations/${orgId}/gcp/projects/${projectId}/verify`, {});
  }

  /** Disconnect one GCP project (monitoring teardown + quota release + promotion). */
  adminDisconnectGcpProject(orgId: string, projectId: string): Observable<AdminGcpDisconnectResult> {
    return this.http.delete<AdminGcpDisconnectResult>(`${this.base}/organizations/${orgId}/gcp/projects/${projectId}`);
  }

  /** Recent GCP project lifecycle audit events for a project. */
  adminGetGcpEvents(orgId: string, days = 7): Observable<{ events: AdminGcpEvent[]; count: number; days: number }> {
    return this.http.get<{ events: AdminGcpEvent[]; count: number; days: number }>(
      `${this.base}/organizations/${orgId}/gcp/events`, { params: { days: String(days) } },
    );
  }

  // ── Monitoring API ────────────────────────────────────────────────────────

  getTierConfigs(): Observable<{ tiers: TierConfig[] }> {
    return this.http.get<{ tiers: TierConfig[] }>(`${this.base}/monitoring/tiers`);
  }

  updateTierConfig(tier: string, maxMonitoredServices: number, maxTeamMembers: number, monthlyCost: number): Observable<TierConfig> {
    return this.http.put<TierConfig>(`${this.base}/monitoring/tiers/${tier}`, { maxMonitoredServices, maxTeamMembers, monthlyCost });
  }

  getMonitoringOverview(): Observable<{ overview: MonitoringOverviewRow[]; tierLimits: Record<string, number> }> {
    return this.http.get<{ overview: MonitoringOverviewRow[]; tierLimits: Record<string, number> }>(
      `${this.base}/monitoring/overview`,
    );
  }

  getUserServiceEntries(userId: string): Observable<{ userId: string; entries: ServiceEntry[] }> {
    return this.http.get<{ userId: string; entries: ServiceEntry[] }>(
      `${this.base}/monitoring/users/${userId}/services`,
    );
  }

  // ── Org monitoring API (platform-admin only) ─────────────────────────────

  getOrgMonitoring(orgId: string): Observable<AdminOrgMonitoring> {
    return this.http.get<AdminOrgMonitoring>(`${this.base}/organizations/${orgId}/monitoring`);
  }

  // ── Monitoring rules API ──────────────────────────────────────────────────

  listMonitoringRules(): Observable<MonitoringRulesResponse> {
    return this.http.get<MonitoringRulesResponse>(`${this.base}/monitoring/rules`);
  }

  updateGlobalMonitoringRule(payload: MonitoringRulePayload): Observable<MonitoringRule> {
    return this.http.put<MonitoringRule>(`${this.base}/monitoring/rules`, payload);
  }

  getMonitoringRuleDetail(serviceType: MonitoringRuleServiceType): Observable<MonitoringRuleDetailResponse> {
    return this.http.get<MonitoringRuleDetailResponse>(`${this.base}/monitoring/rules/${serviceType}`);
  }

  updateServiceTypeMonitoringRule(serviceType: MonitoringRuleServiceType, payload: MonitoringRulePayload): Observable<MonitoringRule> {
    return this.http.put<MonitoringRule>(`${this.base}/monitoring/rules/${serviceType}`, payload);
  }

  getSchedulerConfig(): Observable<SchedulerConfig> {
    return this.http.get<SchedulerConfig>(`${this.base}/monitoring/scheduler-config`);
  }

  updateSchedulerConfig(frequencyMinutes: number): Observable<{ frequencyMinutes: number; expression: string }> {
    return this.http.put<{ frequencyMinutes: number; expression: string }>(
      `${this.base}/monitoring/scheduler-config`,
      { frequencyMinutes },
    );
  }

  listFindings(params?: ListFindingsParams): Observable<ListFindingsResponse> {
    let p = new HttpParams();
    if (params?.severity)    p = p.set('severity',    params.severity);
    if (params?.state)       p = p.set('state',       params.state);
    if (params?.serviceType) p = p.set('serviceType', params.serviceType);
    if (params?.orgId)       p = p.set('orgId',       params.orgId);
    if (params?.limit != null) p = p.set('limit',     String(params.limit));
    if (params?.cursor)      p = p.set('cursor',      params.cursor);
    return this.http.get<ListFindingsResponse>(`${this.base}/findings`, { params: p });
  }

  getFinding(findingId: string, ownerId: string): Observable<AdminFindingDetail> {
    const p = new HttpParams().set('ownerId', ownerId);
    return this.http.get<AdminFindingDetail>(`${this.base}/findings/${findingId}`, { params: p });
  }

  getOrgMonitoringFindings(
    orgId: string,
    params?: { limit?: number; severity?: string; state?: string },
  ): Observable<OrgMonitoringFindingsResponse> {
    let p = new HttpParams();
    if (params?.limit    != null) p = p.set('limit',    String(params.limit));
    if (params?.severity)         p = p.set('severity', params.severity);
    if (params?.state)            p = p.set('state',    params.state);
    return this.http.get<OrgMonitoringFindingsResponse>(
      `${this.base}/organizations/${orgId}/monitoring/findings`,
      { params: p },
    );
  }

  getSystemLogs(params?: SystemLogsParams): Observable<SystemLogsResponse> {
    let p = new HttpParams();
    if (params?.days          != null) p = p.set('days',          String(params.days));
    if (params?.correlationId)         p = p.set('correlationId', params.correlationId);
    if (params?.severity)              p = p.set('severity',      params.severity);
    if (params?.component)             p = p.set('component',     params.component);
    if (params?.eventType)             p = p.set('eventType',     params.eventType);
    return this.http.get<SystemLogsResponse>(`${this.base}/system-logs`, { params: p });
  }

  getSystemLogHealth(): Observable<SystemLogHealthResponse> {
    return this.http.get<SystemLogHealthResponse>(`${this.base}/system-logs/health`);
  }

  listBugReports(params?: BugReportsParams): Observable<BugReportsResponse> {
    let p = new HttpParams();
    if (params?.status)        p = p.set('status', params.status);
    if (params?.cursor)        p = p.set('cursor', params.cursor);
    if (params?.limit != null) p = p.set('limit',  String(params.limit));
    return this.http.get<BugReportsResponse>(`${this.base}/bug-reports`, { params: p });
  }

  updateBugReportStatus(id: string, status: BugReportStatus): Observable<BugReport> {
    return this.http.patch<BugReport>(`${this.base}/bug-reports/${encodeURIComponent(id)}`, { status });
  }
}
